import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Avatar,
  Link,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Place as PlaceIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import apiService from '../services/api';
import type { ProviderPlaceItem } from '../types/providerPlace';
import type { FoundObject, Category } from '../types/foundObject';
import type { ProviderStorageBoxRow } from '../types/storageBox';
import { getAnnouncementThumbUrl } from '../utils/imageUrl';
import FormattedReferenceCode from '../components/FormattedReferenceCode';
import { useProviderPlace } from '../context/ProviderPlaceContext';
import ProviderPlaceChip from '../components/ProviderPlaceChip';
import OpeningHoursEditor from '../components/OpeningHoursEditor';
import { getProviderPlaceDisplayColor, providerPlaceColorSx } from '../utils/providerPlaceColor';
import {
  emptyOpeningHours,
  formatOpeningHoursSummary,
  hasConfiguredOpeningHours,
  normalizeOpeningHours,
  openingHoursSignature,
  readOpeningHoursFromPlace,
  validateOpeningHours,
  type OpeningHours,
} from '../utils/providerPlaceOpeningHours';
import { useProviderInfo } from '../hooks/useProviderInfo';
import { isSplitPlaceStorageMode } from '../utils/placeStorageMode';
import { muiDialogTransitionProps, releaseFocus } from '../utils/dialogA11y';

function compareStorageBoxesByCategory(
  a: ProviderStorageBoxRow,
  b: ProviderStorageBoxRow
): number {
  const byCategory = (a.category?.name ?? '').localeCompare(b.category?.name ?? '', 'fr', {
    sensitivity: 'base',
  });
  if (byCategory !== 0) return byCategory;
  return (a.name ?? '').localeCompare(b.name ?? '', 'fr', { numeric: true, sensitivity: 'base' });
}

const ProviderPlacesPage: React.FC = () => {
  const { selectedPlaceId, hasMultiplePlaces, providerId } = useProviderPlace();
  const { data: providerInfo } = useProviderInfo();
  const isSplitMode = isSplitPlaceStorageMode(providerInfo);
  const theme = useTheme();
  const dialogFullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [boxDialogOpen, setBoxDialogOpen] = useState(false);
  const [filterPlaceId, setFilterPlaceId] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [editingBox, setEditingBox] = useState<ProviderStorageBoxRow | null>(null);
  const [contentBox, setContentBox] = useState<ProviderStorageBoxRow | null>(null);
  const [moveTick, setMoveTick] = useState(0);
  const [boxForm, setBoxForm] = useState({
    provider_item_id: '' as number | '',
    category_id: '' as number | '',
    name: '',
    description: '',
    is_active: true,
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'info' });
  const [hoursDialogOpen, setHoursDialogOpen] = useState(false);
  const [editingPlaceHours, setEditingPlaceHours] = useState<ProviderPlaceItem | null>(null);
  const [placeHoursForm, setPlaceHoursForm] = useState<OpeningHours>(emptyOpeningHours());
  const [savingPlaceHours, setSavingPlaceHours] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const { data: itemsData, isLoading: loadingItems, refetch: refetchItems } = useQuery({
    queryKey: ['provider-items', providerId],
    queryFn: async () => {
      const res = await apiService.getProviderItems(providerId!);
      return res.data.provider_items || [];
    },
    enabled: !!providerId,
  });
  const items = useMemo(() => (itemsData ?? []) as ProviderPlaceItem[], [itemsData]);

  const { data: categoriesData = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => apiService.getCategories(),
    select: (response) => response.data?.categories || [],
    enabled: !!providerId,
  });
  const categories = useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []) as Category[],
    [categoriesData]
  );
  const getCategoryForType = useCallback(
    (objectType: string) => categories.find((c) => c.name === objectType),
    [categories]
  );
  const creatableCategories = useMemo(
    () =>
      categories.filter(
        (c) =>
          c.is_active !== false &&
          c.storage_box_prefix &&
          String(c.storage_box_prefix).replace(/[^A-Za-z]/g, '').length >= 3
      ),
    [categories]
  );

  const {
    data: boxesData,
    isLoading: loadingBoxes,
    refetch: refetchBoxes,
  } = useQuery({
    queryKey: ['provider-storage-boxes', providerId],
    queryFn: async () => {
      const res = await apiService.getStorageBoxesByProvider(providerId!);
      return res.data.storage_boxes || [];
    },
    enabled: !!providerId,
  });
  const boxes = useMemo(() => (boxesData ?? []) as ProviderStorageBoxRow[], [boxesData]);

  const placeFilterOptions = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.display_name || a.name).localeCompare(b.display_name || b.name, 'fr')
      ),
    [items]
  );

  const categoryFilterOptions = useMemo(() => {
    const byId = new Map<number, { id: number; name: string }>();
    for (const box of boxes) {
      const id = box.category_id ?? box.category?.id;
      if (id == null) continue;
      byId.set(id, { id, name: box.category?.name ?? `Catégorie #${id}` });
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name, 'fr'));
  }, [boxes]);

  const hasActiveFilters = filterPlaceId !== '' || filterCategoryId !== '';

  const filteredBoxes = useMemo(() => {
    return boxes
      .filter((row) => {
      if (filterPlaceId !== '') {
        const placeId = row.provider_item_id ?? row.provider_place?.id;
        if (String(placeId) !== filterPlaceId) return false;
      }
      if (filterCategoryId !== '') {
        const catId = row.category_id ?? row.category?.id;
        if (String(catId) !== filterCategoryId) return false;
      }
      return true;
    })
      .sort(compareStorageBoxesByCategory);
  }, [boxes, filterPlaceId, filterCategoryId]);

  const clearFilters = () => {
    setFilterPlaceId('');
    setFilterCategoryId('');
  };

  useEffect(() => {
    if (hasMultiplePlaces && selectedPlaceId != null) {
      setFilterPlaceId(String(selectedPlaceId));
    }
  }, [hasMultiplePlaces, selectedPlaceId]);

  /** Comptage des objets « trouvé » uniquement, pour les pastilles du tableau. */
  const { data: foundObjectsData } = useQuery({
    queryKey: ['found-objects-for-storage-counts'],
    queryFn: () => apiService.getFoundObjects({ limit: 500, status: 'found' }),
    select: (response) => response.data?.data?.found_objects || [],
    enabled: !!providerId,
  });
  const foundObjectsFoundOnly = useMemo(() => (foundObjectsData || []) as FoundObject[], [foundObjectsData]);

  const storageContentMap = useMemo(() => {
    const map = new Map<number, FoundObject[]>();
    for (const obj of foundObjectsFoundOnly) {
      const raw =
        (obj as FoundObject & { storage_box_id?: number }).storage_box_id ??
        obj.objectAttributes?.storage_box_id;
      const id = Number(raw);
      if (!Number.isFinite(id) || id <= 0) continue;
      if (!map.has(id)) {
        map.set(id, []);
      }
      map.get(id)!.push(obj);
    }
    return map;
  }, [foundObjectsFoundOnly]);

  const targetBoxesForMove = useMemo(() => {
    if (!contentBox) return [];
    const cid = contentBox.category_id ?? contentBox.category?.id;
    return boxes.filter((b) => {
      if (b.id === contentBox.id) return false;
      if (cid == null) return true;
      const other = b.category_id ?? b.category?.id;
      return other === cid;
    }).sort(compareStorageBoxesByCategory);
  }, [boxes, contentBox]);

  const { data: boxModalObjects = [], isPending: loadingBoxObjects } = useQuery({
    queryKey: ['found-objects', 'box', contentBox?.id],
    queryFn: async () => {
      const res = await apiService.getFoundObjects({
        storage_box_id: contentBox!.id,
        status: 'found',
        limit: 300,
      });
      return res.data?.data?.found_objects || [];
    },
    enabled: !!contentBox,
  });

  const moveMutation = useMutation({
    mutationFn: ({ id, storage_box_id }: { id: number; storage_box_id: number }) =>
      apiService.updateFoundObject(id, { storage_box_id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
      await queryClient.invalidateQueries({ queryKey: ['found-object-stats'] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects-for-storage-counts'] });
      await queryClient.invalidateQueries({ queryKey: ['found-objects', 'box'] });
      setMoveTick((t) => t + 1);
      showSnackbar('Objet déplacé vers la boîte choisie.', 'success');
    },
    onError: (err: unknown) => {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string } } };
      showSnackbar(ax.response?.data?.error || 'Impossible de déplacer l’objet.', 'error');
    },
  });

  const refreshAll = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['provider-storage-boxes'] });
    await queryClient.invalidateQueries({ queryKey: ['found-objects'] });
    await queryClient.invalidateQueries({ queryKey: ['found-object-stats'] });
    await queryClient.invalidateQueries({ queryKey: ['found-objects-for-storage-counts'] });
    await queryClient.invalidateQueries({ queryKey: ['found-objects', 'box'] });
    await queryClient.invalidateQueries({ queryKey: ['categories'] });
    void refetchBoxes();
    void refetchItems();
  }, [queryClient, refetchBoxes, refetchItems]);

  const handleOpenBoxDialog = (presetPlaceId?: number, box?: ProviderStorageBoxRow) => {
    releaseFocus();
    if (box) {
      setEditingBox(box);
      setBoxForm({
        provider_item_id: box.provider_item_id ?? box.provider_place?.id ?? '',
        category_id: box.category_id ?? box.category?.id ?? '',
        name: box.name || '',
        description: box.description || '',
        is_active: box.is_active !== false,
      });
    } else {
      setEditingBox(null);
      setBoxForm({
        provider_item_id:
          presetPlaceId ??
          (filterPlaceId !== '' ? Number(filterPlaceId) : items[0]?.id ?? ''),
        category_id: '',
        name: '',
        description: '',
        is_active: true,
      });
    }
    setBoxDialogOpen(true);
  };

  const handleCloseBoxDialog = () => {
    releaseFocus();
    setBoxDialogOpen(false);
    setEditingBox(null);
  };

  const handleOpenContentBox = (row: ProviderStorageBoxRow) => {
    releaseFocus();
    setContentBox(row);
  };

  const handleCloseContentBox = () => {
    releaseFocus();
    setContentBox(null);
  };

  const handleSubmitBox = async () => {
    if (!editingBox) {
      const cid =
        typeof boxForm.category_id === 'number' ? boxForm.category_id : Number(boxForm.category_id);
      if (!Number.isFinite(cid) || cid <= 0) {
        showSnackbar('Choisissez une catégorie (préfixe boîte défini en admin Sherlook).', 'warning');
        return;
      }
    }

    if (!isSplitMode) {
      const placeId =
        typeof boxForm.provider_item_id === 'number' ? boxForm.provider_item_id : Number(boxForm.provider_item_id);
      if (!Number.isFinite(placeId) || placeId <= 0) {
        showSnackbar('Sélectionnez le lieu auquel rattacher la boîte.', 'warning');
        return;
      }
    }

    try {
      if (editingBox) {
        const trimmedName = boxForm.name.trim();
        if (!trimmedName) {
          showSnackbar('Le nom de la boîte est requis.', 'warning');
          return;
        }
        await apiService.updateStorageBox(editingBox.id, {
          name: trimmedName,
          description: boxForm.description.trim() || null,
          is_active: boxForm.is_active,
        });
        showSnackbar('Boîte mise à jour', 'success');
      } else {
        const cid = typeof boxForm.category_id === 'number' ? boxForm.category_id : Number(boxForm.category_id);
        if (isSplitMode) {
          if (!providerId) {
            showSnackbar('Partenaire introuvable.', 'error');
            return;
          }
          await apiService.createCentralStorageBox(providerId, {
            category_id: cid,
            description: boxForm.description.trim() || undefined,
            is_active: boxForm.is_active,
          });
        } else {
          const placeId =
            typeof boxForm.provider_item_id === 'number' ? boxForm.provider_item_id : Number(boxForm.provider_item_id);
          await apiService.createStorageBox(placeId, {
            category_id: cid,
            description: boxForm.description.trim() || undefined,
            is_active: boxForm.is_active,
          });
        }
        showSnackbar('Boîte créée', 'success');
      }
      handleCloseBoxDialog();
      await refreshAll();
    } catch (e) {
      console.error(e);
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDeleteBox = async (id: number) => {
    if (!window.confirm('Supprimer cette boîte de stockage ?')) return;
    try {
      await apiService.deleteStorageBox(id);
      showSnackbar('Boîte supprimée', 'success');
      await refreshAll();
    } catch (e: any) {
      console.error(e);
      const apiMessage = e?.response?.data?.error;
      if (typeof apiMessage === 'string' && apiMessage.trim() !== '') {
        showSnackbar(apiMessage, 'warning');
      } else {
        showSnackbar('Impossible la boite conteint des objets', 'warning');
      }
    }
  };

  const handleToggleBox = async (e: React.MouseEvent, row: ProviderStorageBoxRow) => {
    e.stopPropagation();
    try {
      await apiService.updateStorageBox(row.id, { is_active: !row.is_active });
      showSnackbar('Statut mis à jour', 'success');
      await refreshAll();
    } catch (err) {
      console.error(err);
      showSnackbar('Erreur', 'error');
    }
  };

  const handleOpenHoursDialog = (place: ProviderPlaceItem) => {
    releaseFocus();
    setEditingPlaceHours(place);
    setPlaceHoursForm(normalizeOpeningHours(readOpeningHoursFromPlace(place)));
    setHoursDialogOpen(true);
  };

  const handleCloseHoursDialog = () => {
    if (savingPlaceHours) return;
    releaseFocus();
    setHoursDialogOpen(false);
    setEditingPlaceHours(null);
  };

  const handleSubmitPlaceHours = async () => {
    if (!editingPlaceHours) return;
    const validationError = validateOpeningHours(placeHoursForm);
    if (validationError) {
      showSnackbar(validationError, 'warning');
      return;
    }
    setSavingPlaceHours(true);
    try {
      const normalizedOpeningHours = normalizeOpeningHours(placeHoursForm);
      const sentHoursSignature = openingHoursSignature(normalizedOpeningHours);
      const sentHadHours = hasConfiguredOpeningHours(normalizedOpeningHours);
      const response = await apiService.updateProviderItem(editingPlaceHours.id, {
        opening_hours: normalizedOpeningHours,
      });
      const receivedHoursSignature = openingHoursSignature(response.data?.opening_hours);
      if (sentHadHours && receivedHoursSignature !== sentHoursSignature) {
        showSnackbar(
          'Les horaires ne semblent pas avoir été sauvegardés. Vérifiez que la migration API opening_hours est appliquée.',
          'warning',
        );
      } else {
        showSnackbar('Horaires enregistrés', 'success');
        releaseFocus();
        setHoursDialogOpen(false);
        setEditingPlaceHours(null);
      }
      void refetchItems();
    } catch (err) {
      console.error(err);
      const ax = err as { response?: { data?: { error?: string } } };
      showSnackbar(ax.response?.data?.error || 'Erreur lors de la sauvegarde des horaires', 'error');
    } finally {
      setSavingPlaceHours(false);
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <PlaceIcon color="primary" />
        <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.25rem', sm: '2rem' } }}>
          {isSplitMode ? 'Lieux & stockage' : 'Stockage'}
        </Typography>
      </Box>

      {isSplitMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mode <strong>lieux de perte + stockage central</strong> : les lieux (chambres, salles, halls…) sont gérés
          par l&apos;administrateur Sherlook. Vous gérez ici les horaires des lieux et le stockage central des objets.
        </Alert>
      )}

      {items.length === 0 && !isSplitMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Aucun lieu n&apos;est encore défini pour votre établissement. Demandez à l&apos;administrateur Sherlook
          d&apos;ajouter des lieux, puis vous pourrez renseigner les horaires et créer des boîtes de stockage ici.
        </Alert>
      )}

      {items.length === 0 && isSplitMode && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Aucun lieu de perte n&apos;est encore défini. Demandez à l&apos;administrateur Sherlook d&apos;ajouter les
          lieux (chambres, halls, salles…). Vous pouvez déjà configurer le stockage central ci-dessous.
        </Alert>
      )}

      {items.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1.5}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
              {isSplitMode ? 'Lieux de perte — horaires' : "Horaires d'ouverture des lieux"}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Tu peux définir plusieurs plages horaires par jour pour chaque lieu (ex. 9h–12h et 14h–18h).
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Lieu</TableCell>
                  <TableCell>Horaires</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {placeFilterOptions.map((place) => (
                  <TableRow key={place.id}>
                    <TableCell>
                      <ProviderPlaceChip place={place} label={place.display_name || place.name} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatOpeningHoursSummary(normalizeOpeningHours(readOpeningHoursFromPlace(place)))}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => handleOpenHoursDialog(place)}
                        sx={{ textTransform: 'none' }}
                      >
                        Modifier
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Divider sx={{ mb: 3 }} />

      <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
        <Typography variant="h5" component="h2" sx={{ fontSize: { xs: '1.1rem', sm: '1.35rem' } }}>
          {isSplitMode ? 'Stockage central' : 'Boîtes de stockage'}
        </Typography>
      </Box>

      <Box display="flex" justifyContent={{ xs: 'stretch', sm: 'flex-end' }} mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() =>
            handleOpenBoxDialog(filterPlaceId !== '' ? Number(filterPlaceId) : undefined)
          }
          disabled={!isSplitMode && items.length === 0}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Ajouter une boîte
        </Button>
      </Box>

      {boxes.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {!isSplitMode && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Lieu</InputLabel>
                <Select
                  label="Lieu"
                  value={filterPlaceId}
                  onChange={(e) => setFilterPlaceId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Tous les lieux</em>
                  </MenuItem>
                  {placeFilterOptions.map((place) => (
                    <MenuItem key={place.id} value={String(place.id)}>
                      {place.display_name || place.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Catégorie</InputLabel>
                <Select
                  label="Catégorie"
                  value={filterCategoryId}
                  onChange={(e) => setFilterCategoryId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Toutes les catégories</em>
                  </MenuItem>
                  {categoryFilterOptions.map((cat) => (
                    <MenuItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`${filteredBoxes.length} boîte${filteredBoxes.length > 1 ? 's' : ''}`}
                  color={hasActiveFilters ? 'primary' : 'default'}
                  variant={hasActiveFilters ? 'filled' : 'outlined'}
                />
                {hasActiveFilters && (
                  <Button size="small" onClick={clearFilters}>
                    Réinitialiser
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loadingItems || loadingBoxes ? (
        <Typography>Chargement…</Typography>
      ) : (
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}
        >
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Lieu</TableCell>
                <TableCell>Catégorie</TableCell>
                <TableCell>Boîte</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Objets (trouvé)</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {boxes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Aucune boîte — ajoutez-en pour les utiliser lors de la déclaration d&apos;objets trouvés.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredBoxes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary">
                      Aucune boîte ne correspond aux filtres sélectionnés.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBoxes.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleOpenContentBox(row)}
                  >
                    <TableCell>
                      <ProviderPlaceChip
                        place={row.provider_place}
                        label={row.provider_place?.display_name || row.provider_place?.name || '—'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.category?.name || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontWeight: 700,
                          ...providerPlaceColorSx(
                            getProviderPlaceDisplayColor(row.provider_place)
                          ),
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 280 }}>
                        {row.description || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={String(storageContentMap.get(row.id)?.length ?? 0)}
                        size="small"
                        color={(storageContentMap.get(row.id)?.length ?? 0) > 0 ? 'primary' : 'default'}
                        variant={(storageContentMap.get(row.id)?.length ?? 0) > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.is_active !== false ? 'Actif' : 'Inactif'}
                        color={row.is_active !== false ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton size="small" onClick={(e) => void handleToggleBox(e, row)}>
                        {row.is_active !== false ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenBoxDialog(undefined, row);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleDeleteBox(row.id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={!!contentBox}
        onClose={handleCloseContentBox}
        maxWidth="md"
        fullWidth
        fullScreen={dialogFullScreen}
        scroll="paper"
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle component="div" sx={{ pr: 6 }}>
          <Typography variant="h6" component="div">
            {contentBox ? `Objets dans « ${contentBox.name} »` : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Uniquement les objets au statut <strong>Trouvé</strong>. Déplacement possible vers une autre boîte de la
            même catégorie.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {loadingBoxObjects ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={32} />
            </Box>
          ) : boxModalObjects.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Aucun objet « trouvé » dans cette boîte.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={80}>Photo</TableCell>
                    <TableCell>Réf.</TableCell>
                    <TableCell>Titre</TableCell>
                    <TableCell>Fiche</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Déplacer vers</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boxModalObjects.map((obj) => (
                    <TableRow key={obj.id}>
                      <TableCell sx={{ py: 1 }}>
                        <Avatar
                          variant="rounded"
                          src={
                            getAnnouncementThumbUrl(
                              obj.images,
                              getCategoryForType(obj.objectType)?.default_image_url
                            ) || undefined
                          }
                          alt=""
                          sx={{ width: 56, height: 56 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {obj.reference_code ? (
                          <FormattedReferenceCode code={obj.reference_code} variant="body2" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell>{obj.title}</TableCell>
                      <TableCell>
                        <Link
                          component={RouterLink}
                          to={`/found-objects?found_object=${obj.id}`}
                          onClick={handleCloseContentBox}
                          underline="hover"
                        >
                          Voir l&apos;objet
                        </Link>
                      </TableCell>
                      <TableCell>
                        {targetBoxesForMove.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            Aucune autre boîte (même catégorie).
                          </Typography>
                        ) : (
                          <FormControl size="small" fullWidth>
                            <InputLabel id={`move-label-${obj.id}`}>Boîte</InputLabel>
                            <Select
                              key={`move-${obj.id}-${moveTick}`}
                              labelId={`move-label-${obj.id}`}
                              label="Boîte"
                              defaultValue=""
                              disabled={moveMutation.isPending}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (!v) return;
                                moveMutation.mutate({ id: obj.id, storage_box_id: Number(v) });
                              }}
                            >
                              <MenuItem value="">
                                <em>—</em>
                              </MenuItem>
                              {targetBoxesForMove.map((b) => (
                                <MenuItem key={b.id} value={String(b.id)}>
                                  {b.name}
                                  {b.provider_place?.display_name || b.provider_place?.name
                                    ? ` (${b.provider_place?.display_name || b.provider_place?.name})`
                                    : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentBox}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hoursDialogOpen}
        onClose={handleCloseHoursDialog}
        maxWidth="md"
        fullWidth
        fullScreen={dialogFullScreen}
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>
          Horaires — {editingPlaceHours?.display_name || editingPlaceHours?.name || 'Lieu'}
        </DialogTitle>
        <DialogContent>
          <OpeningHoursEditor
            value={placeHoursForm}
            onChange={setPlaceHoursForm}
            disabled={savingPlaceHours}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHoursDialog} disabled={savingPlaceHours}>
            Annuler
          </Button>
          <Button
            onClick={() => {
              releaseFocus();
              void handleSubmitPlaceHours();
            }}
            variant="contained"
            disabled={savingPlaceHours}
          >
            {savingPlaceHours ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={boxDialogOpen}
        onClose={handleCloseBoxDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={dialogFullScreen}
        TransitionProps={muiDialogTransitionProps}
      >
        <DialogTitle>{editingBox ? 'Modifier la boîte' : 'Nouvelle boîte'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {!editingBox && creatableCategories.length === 0 && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  Aucune catégorie avec préfixe boîte (3 lettres). Demandez à l&apos;administrateur Sherlook de
                  renseigner le champ « Préfixe boîtes de stockage » sur les catégories concernées.
                </Alert>
              </Grid>
            )}
            {!isSplitMode && (
            <Grid item xs={12}>
              <FormControl fullWidth required disabled={!!editingBox}>
                <InputLabel>Lieu (ProviderItem)</InputLabel>
                <Select
                  label="Lieu (ProviderItem)"
                  value={boxForm.provider_item_id === '' ? '' : String(boxForm.provider_item_id)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBoxForm((prev) => ({
                      ...prev,
                      provider_item_id: v === '' ? '' : Number(v),
                    }));
                  }}
                >
                  {items.map((it) => (
                    <MenuItem key={it.id} value={String(it.id)}>
                      {it.display_name || it.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            )}
            {editingBox && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nom de la boîte"
                  value={boxForm.name}
                  onChange={(e) => setBoxForm({ ...boxForm, name: e.target.value })}
                  helperText="Libellé affiché lors de la déclaration d'objets trouvés."
                />
              </Grid>
            )}
            {!editingBox && (
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Catégorie (nommage automatique)</InputLabel>
                  <Select
                    label="Catégorie (nommage automatique)"
                    value={boxForm.category_id === '' ? '' : String(boxForm.category_id)}
                    onChange={(e) => {
                      const v = e.target.value;
                      setBoxForm((prev) => ({
                        ...prev,
                        category_id: v === '' ? '' : Number(v),
                      }));
                    }}
                  >
                    <MenuItem value="">
                      <em>—</em>
                    </MenuItem>
                    {creatableCategories.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.name} ({String(c.storage_box_prefix).replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase()}
                        -1, -2…)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Le nom sera généré sur ce lieu : préfixe de la catégorie + numéro (ex. TEL-1). Les boîtes déjà
                  créées pour cette catégorie sur ce lieu déterminent le prochain numéro.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (optionnel)"
                value={boxForm.description}
                onChange={(e) => setBoxForm({ ...boxForm, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={boxForm.is_active}
                    onChange={(e) => setBoxForm({ ...boxForm, is_active: e.target.checked })}
                  />
                }
                label="Actif"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBoxDialog}>Annuler</Button>
          <Button
            onClick={() => void handleSubmitBox()}
            variant="contained"
            disabled={
              editingBox
                ? !boxForm.name.trim()
                : boxForm.category_id === '' ||
                  (!isSplitMode && boxForm.provider_item_id === '') ||
                  creatableCategories.length === 0
            }
          >
            {editingBox ? 'Enregistrer' : 'Créer'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderPlacesPage;
